<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface ProductionStepRow {
  so_production_step_id: Id;
  so_production_step_name?: string;
  so_production_step_description?: string;
  so_production_step_default_duration_minutes?: number;
  so_production_step_qrcode?: string;
  [key: string]: unknown;
}

const emit = defineEmits<{
  'edit': [id: Id];
  'delete': [id: Id];
}>();

const { t } = useI18n();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed<string>(() => '/index.php/so/production_steps/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_production_step_id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_production_step_name',
      title: t('Nome'),
      width: 250,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_production_step_description',
      title: t('Descrição'),
      width: 300,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_production_step_default_duration_minutes',
      title: t('Duração (minutos)'),
      width: 120,
      sortable: true
    },
    {
      data: 'so_production_step_qrcode',
      title: t('QR Code'),
      width: 160,
      sortable: true,
      filterType: 'text',
      renderType: 'copy_on_click'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_production_steps_${new Date().toISOString().split('T')[0]}`,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.so_production_step_id;
      return id == null ? '' : `so-production-steps-row-${id}`;
    },
    columns: tableColumns.value,
    defaultSortField: 'so_production_step_id',
    defaultSortOrder: -1 as const,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: t('CSV') }));

const loadData = (): void => {
  log('SOProductionStepsTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: ProductionStepRow[]): void => {
  log('SOProductionStepsTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOProductionStepsTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar etapas de produção') + ': ' + message);
};

const handleEditClick = (id: Id): void => {
  log('SOProductionStepsTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

const handleSaved = (): void => loadData();
const handleDeleted = (): void => loadData();

onMounted(() => {
  log('SOProductionStepsTable', 'mounted', 'Setting up event listeners');
  eventBus.on('so-production-steps.saved', handleSaved);
  eventBus.on('so-production-steps.deleted', handleDeleted);
});

onBeforeUnmount(() => {
  eventBus.off('so-production-steps.saved', handleSaved);
  eventBus.off('so-production-steps.deleted', handleDeleted);
});

defineExpose({ loadData });
</script>
